import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Archive,
  Bot,
  Copy,
  Ellipsis,
  ExternalLink,
  MessagesSquare,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FLOW_STATUS_LABEL, type FlowSummary } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { CopyButton } from "@/components/flows/fields";
import { NewFlowDialog } from "@/components/flows/NewFlowDialog";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Hint } from "@/components/ui/tooltip";
import { useAgents } from "@/lib/agent";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { relativeTime } from "@/lib/crm";
import {
  errorMessage,
  useArchiveFlow,
  useDeleteFlow,
  useDuplicateFlow,
  useFlows,
} from "@/lib/flows";

export const Route = createFileRoute("/_authenticated/typechat/")({
  component: TypechatPage,
});

const TOM_STATUS: Record<FlowSummary["status"], NonNullable<BadgeProps["variant"]>> = {
  published: "success",
  draft: "muted",
  archived: "outline",
};

/**
 * Typechat: conversas automáticas no WhatsApp e no chat do site.
 *
 * Mesmo padrão da lista de Formulários — o número da semana fica na linha
 * (iniciadas → concluídas → leads) para responder "o que está no ar e está
 * trazendo gente?" sem abrir o relatório de cada um.
 */
function TypechatPage() {
  const navigate = useNavigate();
  const [novo, setNovo] = useState(false);
  const [excluindo, setExcluindo] = useState<FlowSummary | null>(null);

  const fluxos = useFlows({ kind: "chat" });
  // Agentes (tela "Agente de IA") também são Typechats: ganham um selo e o
  // atalho para a tela simples. A lista de agentes só existe para a gestão.
  const { can } = useAuth();
  const agentes = useAgents(can("admin", "manager"));
  const ehAgente = useMemo(() => new Set((agentes.data ?? []).map((a) => a.id)), [agentes.data]);
  const duplicar = useDuplicateFlow();
  const arquivar = useArchiveFlow();
  const excluir = useDeleteFlow();

  const linhas = useMemo(
    () =>
      (fluxos.data ?? [])
        // O filtro também vai na consulta; aqui garante caso o servidor ignore.
        .filter((f) => f.kind === "chat")
        // Arquivados por último: estão aqui só para consulta.
        .sort((a, b) => Number(a.status === "archived") - Number(b.status === "archived")),
    [fluxos.data],
  );
  const noAr = linhas.filter((f) => f.status === "published").length;

  const acoes = {
    abrir: (f: FlowSummary) => void navigate({ to: "/typechat/$flowId", params: { flowId: f.id } }),
    duplicar: (f: FlowSummary) =>
      duplicar.mutate(f.id, {
        onSuccess: (copia) => {
          toast.success("Cópia criada como rascunho");
          void navigate({ to: "/typechat/$flowId", params: { flowId: copia.id } });
        },
        onError: (e) => toast.error(errorMessage(e, "Não foi possível duplicar")),
      }),
    arquivar: (f: FlowSummary) =>
      arquivar.mutate(f.id, {
        onSuccess: () => toast.success("Arquivado: o Typechat para de iniciar conversas novas"),
        onError: (e) => toast.error(errorMessage(e, "Não foi possível arquivar")),
      }),
    excluir: (f: FlowSummary) => setExcluindo(f),
  };

  return (
    <>
      <PageHeader
        title="Typechat"
        description={
          fluxos.data && linhas.length > 0
            ? `${noAr} no ar de ${linhas.length} · números dos últimos 7 dias`
            : "Conversas automáticas no WhatsApp e no site — com fluxo fixo ou IA"
        }
        action={
          <Button onClick={() => setNovo(true)}>
            <Plus />
            Novo Typechat
          </Button>
        }
      />

      <PageBody className="space-y-4">
        <p className="flex items-start gap-2 rounded-lg bg-warning/18 px-3 py-2 text-xs text-warning-foreground dark:text-warning">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>
            No WhatsApp não oficial (Evolution), robô automático pode levar ao banimento do número —
            por padrão o Typechat só roda no número oficial.
          </span>
        </p>

        <Card className="overflow-hidden">
          {fluxos.isPending ? (
            <div className="divide-y" aria-busy>
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                  <Skeleton className="size-9 rounded-lg" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-48 max-w-[60%]" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="hidden h-5 w-20 rounded-full md:block" />
                  <Skeleton className="hidden h-5 w-32 rounded-full lg:block" />
                </div>
              ))}
            </div>
          ) : fluxos.isError ? (
            <EmptyState
              icon={MessagesSquare}
              title="Não foi possível carregar os Typechats"
              description="Confira a conexão e tente de novo."
              action={
                <Button variant="outline" size="sm" onClick={() => void fluxos.refetch()}>
                  Tentar de novo
                </Button>
              }
            />
          ) : linhas.length === 0 ? (
            <div className="px-4 py-12 sm:px-6">
              <EmptyState
                icon={MessagesSquare}
                title="Nenhum Typechat ainda"
                description="Monte a conversa que atende sozinho quem chega pelo WhatsApp ou pelo chat do site: dá as boas-vindas, faz as perguntas, separa quem está pronto e passa para a equipe na hora certa. Cada conversa vira lead no CRM, com a campanha de onde veio."
                className="py-0"
              />
              <div className="mx-auto mt-6 grid max-w-2xl gap-3 sm:grid-cols-2">
                <Caminho
                  icon={MessagesSquare}
                  titulo="Fluxo guiado"
                  texto="Mensagens, perguntas com botões e desvios que você desenha. Previsível e sem custo de IA — bom para qualificar e agendar."
                  onClick={() => setNovo(true)}
                />
                <Caminho
                  icon={Bot}
                  titulo="Com IA"
                  texto="Um agente de IA conversa em linguagem natural, entende o caso e volta ao fluxo com as respostas. Com travas: não promete resultado nem inventa preço."
                  onClick={() => setNovo(true)}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5">Nome</th>
                      <th className="px-4 py-2.5">Situação</th>
                      <th className="px-4 py-2.5">Versão</th>
                      <th className="px-4 py-2.5">Últimos 7 dias</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {linhas.map((f) => (
                      <tr
                        key={f.id}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("a, button, [role=menuitem]"))
                            return;
                          acoes.abrir(f);
                        }}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                              <MessagesSquare className="size-4" />
                            </span>
                            <div className="min-w-0">
                              <Link
                                to="/typechat/$flowId"
                                params={{ flowId: f.id }}
                                className="block max-w-72 truncate font-semibold hover:text-primary"
                              >
                                {f.name}
                              </Link>
                              {ehAgente.has(f.id) && <SeloAgente flowId={f.id} />}
                              <p className="truncate text-xs text-muted-foreground">
                                /f/{f.slug} · editado {relativeTime(f.updatedAt)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant={TOM_STATUS[f.status]}>
                            {FLOW_STATUS_LABEL[f.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                          {f.publishedVersion ? `v${f.publishedVersion}` : "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          <Semana stats={f.stats} />
                        </td>
                        <td className="px-4 py-2.5">
                          <Acoes fluxo={f} acoes={acoes} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="divide-y md:hidden">
                {linhas.map((f) => (
                  <li key={f.id} className="flex items-start gap-2 px-4 py-3">
                    <Link
                      to="/typechat/$flowId"
                      params={{ flowId: f.id }}
                      className="min-w-0 flex-1"
                    >
                      <p className="truncate text-sm font-semibold">{f.name}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant={TOM_STATUS[f.status]}>{FLOW_STATUS_LABEL[f.status]}</Badge>
                        {ehAgente.has(f.id) && (
                          <Badge variant="soft">
                            <Sparkles className="size-3" />
                            Agente de IA
                          </Badge>
                        )}
                        {f.publishedVersion && (
                          <Badge variant="outline">v{f.publishedVersion}</Badge>
                        )}
                      </div>
                      <div className="mt-1.5">
                        <Semana stats={f.stats} />
                      </div>
                    </Link>
                    <Acoes fluxo={f} acoes={acoes} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </PageBody>

      <NewFlowDialog open={novo} onOpenChange={setNovo} area="chat" />

      <Dialog open={Boolean(excluindo)} onOpenChange={(o) => !o && setExcluindo(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir “{excluindo?.name}”?</DialogTitle>
            <DialogDescription>
              Some da lista, o link do chat deixa de abrir e as palavras-chave param de iniciar a
              conversa. Não dá para desfazer. Se ele já conversou com alguém, arquive em vez de
              excluir — as conversas e os leads continuam.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={excluir.isPending}
              onClick={() =>
                excluindo &&
                excluir.mutate(excluindo.id, {
                  onSuccess: () => {
                    toast.success("Typechat excluído");
                    setExcluindo(null);
                  },
                  onError: (e) => {
                    toast.error(
                      e instanceof ApiError && e.status === 409
                        ? "Este Typechat já tem conversas. Arquive em vez de excluir."
                        : errorMessage(e, "Não foi possível excluir"),
                    );
                    setExcluindo(null);
                  },
                })
              }
            >
              {excluir.isPending ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Selo dos fluxos criados pela tela "Agente de IA", com o atalho para ela:
 * editar aqui mexe no grafo à mão, e a tela do agente avisa antes de
 * sobrescrever.
 */
function SeloAgente({ flowId }: { flowId: string }) {
  return (
    <Link
      to="/agente-ia/$flowId"
      params={{ flowId }}
      className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/15"
    >
      <Sparkles className="size-3" />
      Agente de IA · abrir na tela do agente
    </Link>
  );
}

/** Um dos dois caminhos do estado vazio. */
function Caminho({
  icon: Icon,
  titulo,
  texto,
  onClick,
}: {
  icon: typeof Bot;
  titulo: string;
  texto: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer flex-col items-start gap-1.5 rounded-xl border bg-card p-4 text-left transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-panel focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
    >
      <span className="inline-flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-4" />
      </span>
      <span className="text-sm font-semibold">{titulo}</span>
      <span className="text-xs text-muted-foreground">{texto}</span>
    </button>
  );
}

/** "12 → 8 → 7 · 67%": iniciadas, concluídas, leads e a taxa de conclusão. */
function Semana({ stats }: { stats: FlowSummary["stats"] }) {
  const taxa = stats.starts > 0 ? Math.round((stats.completes / stats.starts) * 100) : null;
  if (stats.starts === 0) {
    return <span className="text-xs text-muted-foreground">sem conversas na semana</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-xs tabular-nums">
      <Hint label="Iniciadas → concluídas → leads">
        <span>
          <span className="font-medium">{stats.starts.toLocaleString("pt-BR")}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="font-medium">{stats.completes.toLocaleString("pt-BR")}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="font-semibold text-primary">{stats.leads.toLocaleString("pt-BR")}</span>
        </span>
      </Hint>
      {taxa !== null && <span className="text-muted-foreground">· {taxa}% concluem</span>}
    </span>
  );
}

function Acoes({
  fluxo: f,
  acoes,
}: {
  fluxo: FlowSummary;
  acoes: Record<"abrir" | "duplicar" | "arquivar" | "excluir", (f: FlowSummary) => void>;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <CopyButton text={f.publicUrl} label="Copiar link do chat" size="icon-xs" variant="ghost" />
      <DropdownMenu>
        <Hint label="Mais ações">
          <DropdownMenuTrigger asChild>
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={`Ações de ${f.name}`}
              className="text-muted-foreground"
            >
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
        </Hint>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => acoes.abrir(f)}>
            <Pencil />
            Abrir editor
          </DropdownMenuItem>
          {f.status === "published" && (
            <DropdownMenuItem asChild>
              <a href={f.publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink />
                Ver página
              </a>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => acoes.duplicar(f)}>
            <Copy />
            Duplicar
          </DropdownMenuItem>
          {f.status !== "archived" && (
            <DropdownMenuItem onSelect={() => acoes.arquivar(f)}>
              <Archive />
              Arquivar
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={() => acoes.excluir(f)}>
            <Trash2 />
            Excluir
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
